/**
 * ContactDrawer — 复古通联抽屉（工单 6）
 * 街机投币口 / 传真机插槽视觉 · 官方邮箱 jimlai@xin.xyz
 * [投递羊皮信 / 复制邮箱] + [发送邮件] + 生态印章（KernelHub / iCloser）
 * 配色遵循负向清单（禁一切荧光绿）：全息冷青 #00f0ff / 极光紫 #8b5cf6 /
 * 钛合金白 #f1f5f9 / 极客冷灰 #94a3b8 / 琥珀金 #fbbf24（邮箱凭证点睛）/ 橙红 #f97316（仅警示）
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Copy, Mail, Check, ExternalLink } from "lucide-react";
import { audioEngine } from "@/lib/audioEngine";

/** 官方联络邮箱 —— 全组件任何出现邮箱处必须一字不差 */
export const CONTACT_EMAIL = "jimlai@xin.xyz";
/** 一键系统邮件客户端（subject 的 %20 与方括号按工单原文） */
export const MAILTO_HREF =
  "mailto:jimlai@xin.xyz?subject=[Xin.xyz]%20Collaboration";
/** 印章 A：KernelHub 神枢控制台直达链接 */
export const KERNELHUB_STAMP_URL = "https://www.icloser.xyz/kernelhub.html";
/** 印章 B：iCloser 钱包基础设施直达链接 */
export const ICLOSER_STAMP_URL = "https://icloser.xyz";
/** 页脚 about 入口：about xin.xyz → https://xin.xyz/about.html（站内 about 页） */
export const ABOUT_BOOK_URL = "https://xin.xyz/about.html";
/** 页脚 about 概括（减少认知偏差） */
export const ABOUT_SUMMARY_LINES: readonly string[] = [
  "about xin.xyz 是 XIN.XYZ 的官方说明页：讲清「这是什么 / 能做什么 / 为何这么设计 / 如何上手」。",
  "核心：可玩的 Neo-Pixel 座舱 —— 全息哨兵「信（XIN）」守护自托管金库、转接本地 CLI 权限、掉落真实 KernelHub 创世金钥（999 枚）。",
  "能力：7 档权限中继控制台（Read/Edit/Exec/Net/Push/Ops/Destructive）、创世金钥核销、60FPS Canvas 战斗手感（打击停顿/震屏/跳字/双端操控）。",
  "论点：AI（自主生产力）× 区块链（可结算、可归属的所有权）—— Agent 就是新的交易对手方。",
  "上手：游戏内领创世凭证 → 手机端 KernelHub App 激活 → 桌面跑 icloser pair，钥匙圈自动继承。",
];

/** 复制成功 Toast 文案（工单原文，邮箱一字不差） */
const COPIED_TOAST = "信件凭证已装入剪贴板（jimlai@xin.xyz），期待与指挥官建联！";
const COPY_FAILED_TOAST = "复制失败，请手动选取邮箱地址。";
/** Toast 2.5s 自动消失（与 CSS 淡入淡出动画时长一致） */
const TOAST_MS = 2500;

interface ToastMessage {
  /** 自增 id：同文案重复触发时用作 key 重置淡入淡出动画 */
  id: number;
  text: string;
  /** copied=信件凭证（琥珀金）；failed=告警（橙红，仅用于警示） */
  kind: "copied" | "failed";
}

/** 像素信封口 V 形折线（16×12 viewBox 内的 1px 点阵） */
const ENVELOPE_FLAP: ReadonlyArray<readonly [number, number]> = [
  [2, 3], [3, 4], [4, 5], [5, 6], [6, 7], [7, 8],
  [13, 3], [12, 4], [11, 5], [10, 6], [9, 7], [8, 8],
];

/** 邮件图标像素画：羊皮信封（纯 SVG 点阵，crispEdges 抗糊；琥珀金蜡封） */
function PixelEnvelope() {
  return (
    <svg
      viewBox="0 0 16 12"
      width={64}
      height={48}
      shapeRendering="crispEdges"
      role="img"
      aria-label="像素羊皮信封"
      className="px-art"
    >
      {/* 信封体（羊皮纸 · 邮箱凭证暖金系） */}
      <rect x={1} y={2} width={14} height={9} fill="#ffe9c2" />
      {/* 外框描边 */}
      <rect x={1} y={2} width={14} height={1} fill="#101010" />
      <rect x={1} y={10} width={14} height={1} fill="#101010" />
      <rect x={1} y={2} width={1} height={9} fill="#101010" />
      <rect x={14} y={2} width={1} height={9} fill="#101010" />
      {/* 信封口 V 形折线 */}
      {ENVELOPE_FLAP.map(([x, y]) => (
        <rect key={`${x}-${y}`} x={x} y={y} width={1} height={1} fill="#101010" />
      ))}
      {/* 蜡封 · 琥珀金 #fbbf24 */}
      <rect x={7} y={9} width={2} height={1} fill="#fbbf24" />
    </svg>
  );
}

/** 街机投币口 / 传真机插槽装饰条 */
function SlotRail({
  label,
  tone = "amber",
}: {
  label: string;
  tone?: "amber" | "dim";
}) {
  return (
    <div className="contact-slot-rail" aria-hidden="true">
      <span className="contact-rivet" />
      <div className="contact-slot-mouth" />
      <span
        className={`whitespace-nowrap font-pixel text-[8px] tracking-widest sm:text-[9px] ${
          tone === "amber" ? "contact-amber" : "contact-dim"
        }`}
      >
        {label}
      </span>
      <div className="contact-slot-mouth" />
      <span className="contact-rivet" />
    </div>
  );
}

export function ContactDrawer() {
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [copied, setCopied] = useState(false);
  const toastSeq = useRef(0);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), TOAST_MS);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const showToast = useCallback((kind: ToastMessage["kind"], text: string) => {
    toastSeq.current += 1;
    setToast({ id: toastSeq.current, text, kind });
  }, []);

  /** 复制邮箱：Clipboard API → execCommand 兜底 → 失败提示 */
  const copyEmail = useCallback(async () => {
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(CONTACT_EMAIL);
        ok = true;
      }
    } catch {
      ok = false; // 落入 execCommand 兜底
    }
    if (!ok) {
      try {
        const ta = document.createElement("textarea");
        ta.value = CONTACT_EMAIL;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        ta.style.top = "0";
        document.body.appendChild(ta);
        ta.select();
        ta.setSelectionRange(0, ta.value.length); // iOS 选区兼容
        ok = document.execCommand("copy");
        document.body.removeChild(ta);
      } catch {
        ok = false;
      }
    }
    if (ok) {
      setCopied(true);
      audioEngine.play("coin");
      showToast("copied", COPIED_TOAST);
      window.setTimeout(() => setCopied(false), 2000);
    } else {
      audioEngine.play("alert");
      showToast("failed", COPY_FAILED_TOAST);
    }
  }, [showToast]);

  return (
    <footer className="contact-drawer relative w-full min-w-0" aria-label="Contact Drawer · 通联抽屉">
      {/* 投币口外框：深色金属底 + 霓虹描边 */}
      <div className="px-border-out contact-shell overflow-hidden bg-[#0d111a] px-4 py-5 sm:px-8 sm:py-6">
        {/* 顶部投币口装饰条 */}
        <SlotRail label="INSERT COIN · 投币口" tone="amber" />

        <div className="mt-5 grid min-w-0 gap-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
          {/* ── 邮箱区：像素信封 + 居中邮箱 ── */}
          <div className="contact-hero min-w-0">
            <PixelEnvelope />
            <div className="contact-violet font-pixel text-[8px] tracking-widest">
              OFFICIAL CHANNEL · 官方联络通道
            </div>
            <div className="contact-email contact-cyan contact-glow-cyan font-pixel text-[12px] sm:text-[16px]">
              {CONTACT_EMAIL}
            </div>
            <p className="contact-dim font-zpix text-[12px] leading-6">
              投递合作与共创信件 · 信件通道常开
            </p>
            <div className="flex w-full flex-wrap justify-center gap-3">
              <button
                type="button"
                className="px-btn px-btn-primary"
                onClick={copyEmail}
                onMouseEnter={() => audioEngine.play("beep")}
              >
                {copied ? (
                  <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                ) : (
                  <Copy className="h-3.5 w-3.5" strokeWidth={2.5} />
                )}
                <span>投递羊皮信 / 复制邮箱</span>
              </button>
              <a
                className="px-btn"
                href={MAILTO_HREF}
                onMouseEnter={() => audioEngine.play("beep")}
                onClick={() => audioEngine.play("select")}
              >
                <Mail className="h-3.5 w-3.5" strokeWidth={2.5} />
                <span>发送邮件</span>
              </a>
            </div>
          </div>

          {/* ── 生态印章：双重校验直达链路 ── */}
          <div className="min-w-0">
            <div className="contact-violet font-pixel text-[8px] tracking-widest">
              ECO STAMPS · 生态印章
            </div>
            <div className="mt-3 grid min-w-0 gap-3 sm:grid-cols-2">
              {/* 印章 A【KernelHub 神枢控制台】= 本地 CLI 代理随身中继（冰青系 #38bdf8） */}
              <a
                className="contact-stamp contact-stamp-a"
                href={KERNELHUB_STAMP_URL}
                target="_blank"
                rel="noopener noreferrer"
                onMouseEnter={() => audioEngine.play("beep")}
                onClick={() => audioEngine.play("select")}
              >
                <div className="flex items-center gap-2">
                  <span className="contact-led-ice" />
                  <span className="contact-ice font-pixel text-[8px]">
                    STAMP A
                  </span>
                  <ExternalLink className="contact-ice h-3 w-3" strokeWidth={2.5} />
                </div>
                <div className="contact-bright mt-2 font-zpix text-[12px]">
                  KernelHub 神枢控制台
                </div>
                <div className="contact-dim mt-1 font-zpix text-[11px]">
                  本地 CLI 代理 · 随身中继
                </div>
                <div className="contact-dim mt-1 break-all font-pixel text-[7px]">
                  www.icloser.xyz/kernelhub.html
                </div>
              </a>
              {/* 印章 B【iCloser 钱包基础设施】= 自托管资产背包（琥珀金系） */}
              <a
                className="contact-stamp contact-stamp-b"
                href={ICLOSER_STAMP_URL}
                target="_blank"
                rel="noopener noreferrer"
                onMouseEnter={() => audioEngine.play("beep")}
                onClick={() => audioEngine.play("select")}
              >
                <div className="flex items-center gap-2">
                  <span className="contact-led-amber" />
                  <span className="contact-amber font-pixel text-[8px]">
                    STAMP B
                  </span>
                  <ExternalLink className="contact-amber h-3 w-3" strokeWidth={2.5} />
                </div>
                <div className="contact-bright mt-2 font-zpix text-[12px]">
                  iCloser 钱包基础设施
                </div>
                <div className="contact-dim mt-1 font-zpix text-[11px]">
                  自托管资产背包
                </div>
                <div className="contact-dim mt-1 break-all font-pixel text-[7px]">
                  icloser.xyz
                </div>
              </a>
              {/* 印章 C【iCloser RPC Manager】= 可移植多链 RPC 节点控制面（站内 /rpc/） */}
              <a
                className="contact-stamp contact-stamp-a"
                href="/rpc/"
                target="_blank"
                rel="noopener noreferrer"
                onMouseEnter={() => audioEngine.play("beep")}
                onClick={() => audioEngine.play("select")}
              >
                <div className="flex items-center gap-2">
                  <span className="contact-led-ice" />
                  <span className="contact-ice font-pixel text-[8px]">
                    STAMP C
                  </span>
                  <ExternalLink className="contact-ice h-3 w-3" strokeWidth={2.5} />
                </div>
                <div className="contact-bright mt-2 font-zpix text-[12px]">
                  iCloser RPC Manager
                </div>
                <div className="contact-dim mt-1 font-zpix text-[11px]">
                  Portable multi-chain node control plane
                </div>
                <div className="contact-dim mt-1 break-all font-pixel text-[7px]">
                  xin.xyz/rpc/
                </div>
              </a>
            </div>
          </div>
        </div>

        {/* 底部出纸口装饰条 */}
        <div className="mt-6">
          <SlotRail label="FAX SLOT · 出纸口" tone="dim" />
        </div>

        {/* 页脚 about 入口 + 官方链路（首页页角 / 页脚双入口闭环） */}
        <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
          <a
            className="px-btn inline-flex min-h-[44px] items-center gap-2"
            href={ABOUT_BOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            onMouseEnter={() => audioEngine.play("beep")}
            onClick={() => audioEngine.play("select")}
          >
            <ExternalLink className="h-3.5 w-3.5" strokeWidth={2.5} />
            <span>about xin.xyz</span>
          </a>
          <a
            className="px-btn inline-flex min-h-[44px] items-center gap-2"
            href="https://book.xin.xyz"
            target="_blank"
            rel="noopener noreferrer"
            onMouseEnter={() => audioEngine.play("beep")}
            onClick={() => audioEngine.play("select")}
          >
            <ExternalLink className="h-3.5 w-3.5" strokeWidth={2.5} />
            <span>book.xin.xyz</span>
          </a>
          <a
            className="px-btn inline-flex min-h-[44px] items-center gap-2"
            href={MAILTO_HREF}
            onMouseEnter={() => audioEngine.play("beep")}
            onClick={() => audioEngine.play("select")}
          >
            <Mail className="h-3.5 w-3.5" strokeWidth={2.5} />
            <span>MAIL {CONTACT_EMAIL}</span>
          </a>
        </div>

        {/* about 页内容概括（减少认知偏差） */}
        <div className="mt-5">
          <div className="contact-violet font-pixel text-[8px] tracking-widest">
            ABOUT XIN.XYZ // 页面概括
          </div>
          <ul className="mt-2 space-y-1.5">
            {ABOUT_SUMMARY_LINES.map((line) => (
              <li
                key={line}
                className="contact-dim flex gap-2 font-zpix text-[11px] leading-5"
              >
                <span className="contact-amber shrink-0">▸</span>
                <span>{line}</span>
              </li>
            ))}
          </ul>
          <p className="contact-dim mt-2 font-zpix text-[10px] leading-5">
            完整版（英文）：{" "}
            <a
              className="contact-cyan underline-offset-2 hover:underline"
              href={ABOUT_BOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {ABOUT_BOOK_URL}
            </a>
          </p>
        </div>

        <div className="contact-dim mt-4 border-t-2 border-black pt-4 text-center font-pixel text-[7px] leading-4">
          © XIN.XYZ · MAIL {CONTACT_EMAIL} · KERNELHUB + ICLOSER
          <br />
          <span className="contact-ice">NEURAL AGENT &amp; SELF-CUSTODY VAULT</span>
          {" · "}
          <a
            className="contact-amber underline-offset-2 hover:underline"
            href={ABOUT_BOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            about xin.xyz
          </a>
        </div>
      </div>

      {/* 悬浮像素 Toast：fixed + pointer-events:none + 高于 CRT 扫描线层（z 50-52） */}
      {toast && (
        <div
          key={toast.id}
          role="status"
          aria-live="polite"
          className="contact-toast bg-[#121826] px-4 py-3"
        >
          <div
            className={`font-pixel text-[8px] ${
              toast.kind === "copied" ? "contact-amber" : "contact-alert"
            }`}
          >
            {toast.kind === "copied" ? "TOAST · 信件凭证" : "TOAST · 复制告警"}
          </div>
          <p className="contact-bright mt-1 font-zpix text-[12px] leading-6">
            {toast.text}
          </p>
        </div>
      )}
    </footer>
  );
}

export default ContactDrawer;
